/**
 * TaxInvoiceModal
 * ---------------
 * One-time tax-invoice billing-details capture, opened from the
 * /shop and /shop/addons Buy Now flow when the org's
 * `is_tax_invoice_submitted` flag is false. On successful save the
 * parent re-runs the Buy flow (Razorpay popup proceeds).
 *
 * Fields (mirror BE TaxInvoiceRequest):
 *   • is_gst_registered  (checkbox)
 *   • tax_invoice_gst_number  (15-char GSTIN — only when checked)
 *   • tax_invoice_address (textarea, min 5 chars)
 *   • tax_invoice_state   (dropdown — INDIA_STATES)
 *   • tax_invoice_city    (dropdown filtered by state, free-text fallback)
 *   • tax_invoice_pincode (6-digit numeric input)
 *
 * Props:
 *   open     (bool)
 *   onCancel (fn)        — closes without saving (Buy flow aborts)
 *   onSaved  (fn)        — fires AFTER server confirms 200; parent
 *                          re-triggers Buy (Razorpay popup opens)
 *   initialValue (obj?)  — optional pre-fill (re-open / edit)
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Form, Input, Modal, Select, Switch, Typography,
  message as antMessage,
} from "antd";
import { postData, updateData } from "../../api/apiService";
import { ADMIN, PAYMENTS } from "../../api/endpointMap";
import {
  INDIA_STATES,
  citiesForState,
  pincodesForCity,
} from "../../data/indiaGeo";

const { Title, Paragraph, Text } = Typography;

const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

const OTHER_CITY = "__OTHER__";

export default function TaxInvoiceModal({
  open,
  onCancel,
  onSaved,
  initialValue,
  // `mode="admin"` routes the save to PUT /admin/organizations/{id}/tax-invoice
  // (super_admin only). Default `"tenant"` posts to the tenant endpoint,
  // which 409s on re-submit. Admin mode requires `orgId` prop.
  mode = "tenant",
  orgId,
  // Read-only display of the org's legal name (organizations.company_name).
  // Source of truth is always the BE — modal never edits this. Used here
  // only so the user verifies they're filling details for the right org.
  companyName,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [state, setState] = useState(initialValue?.tax_invoice_state || null);
  // Default toggle to ON (Yes) when no initialValue is supplied — most
  // signups for our customer base are GST-registered firms. Admin / edit
  // mode preserves whatever was previously saved (`?? true` only kicks in
  // when the field is genuinely undefined).
  const [isGstRegistered, setIsGstRegistered] = useState(
    initialValue?.is_gst_registered ?? true,
  );
  // Reactive reads of form values — needed for conditional render of
  // the "Type below" fallback panels. `form.getFieldValue(...)` is
  // NOT reactive (doesn't trigger re-render when the field changes),
  // so the panel would never appear after picking "Other (type below)".
  const cityWatch = Form.useWatch("tax_invoice_city", form);
  const pinWatch = Form.useWatch("tax_invoice_pincode", form);

  // Reset form whenever the modal opens (so a stale draft doesn't
  // leak between sessions). Pre-fill from initialValue if supplied.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        is_gst_registered: initialValue?.is_gst_registered ?? true,
        tax_invoice_gst_number: initialValue?.tax_invoice_gst_number || "",
        tax_invoice_address: initialValue?.tax_invoice_address || "",
        tax_invoice_state: initialValue?.tax_invoice_state || undefined,
        tax_invoice_city: initialValue?.tax_invoice_city || undefined,
        tax_invoice_pincode: initialValue?.tax_invoice_pincode || "",
      });
      setState(initialValue?.tax_invoice_state || null);
      setCity(initialValue?.tax_invoice_city || null);
      setCustomCity("");
      setCustomPin("");
      setIsGstRegistered(initialValue?.is_gst_registered ?? true);
      setServerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cityOptions = useMemo(() => {
    const list = citiesForState(state || "");
    const opts = list.map((c) => ({ value: c, label: c }));
    opts.push({ value: OTHER_CITY, label: "Other (type below)" });
    return opts;
  }, [state]);

  const [customCity, setCustomCity] = useState("");
  const [city, setCity] = useState(initialValue?.tax_invoice_city || null);
  const handleCityChange = (val) => {
    if (val !== OTHER_CITY) {
      setCustomCity("");
      setCity(val);
    } else {
      setCity(null); // typed city — no curated pincodes available
    }
    form.setFieldsValue({ tax_invoice_city: val, tax_invoice_pincode: undefined });
  };

  // Pincode dropdown options — filtered by the currently-picked city.
  // Falls back to just the "Other" sentinel when the city has no
  // curated pincodes; the form then renders a free-text input.
  const OTHER_PIN = "__OTHER_PIN__";
  const [customPin, setCustomPin] = useState("");
  const pincodeOptions = useMemo(() => {
    const list = city ? pincodesForCity(city) : [];
    const opts = list.map((p) => ({ value: p, label: p }));
    opts.push({ value: OTHER_PIN, label: "Other (type below)" });
    return opts;
  }, [city]);
  const handlePincodeChange = (val) => {
    if (val !== OTHER_PIN) setCustomPin("");
    form.setFieldsValue({ tax_invoice_pincode: val });
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // form shows inline errors
    }
    // Resolve the "Other" sentinel into the typed value
    let cityFinal = values.tax_invoice_city;
    if (cityFinal === OTHER_CITY) {
      cityFinal = (customCity || "").trim();
      if (!cityFinal) {
        antMessage.error("Please type your city name.");
        return;
      }
    }
    // Same sentinel resolution for pincode
    let pinFinal = values.tax_invoice_pincode;
    if (pinFinal === OTHER_PIN) {
      pinFinal = (customPin || "").trim();
      if (!pinFinal) {
        antMessage.error("Please type your pincode.");
        return;
      }
    }
    if (!/^[1-9][0-9]{5}$/.test(pinFinal)) {
      antMessage.error("Enter a valid 6-digit Indian pincode.");
      return;
    }

    setSubmitting(true);
    setServerError(null);
    try {
      const body = {
        is_gst_registered: !!values.is_gst_registered,
        tax_invoice_gst_number: values.is_gst_registered
          ? (values.tax_invoice_gst_number || "").trim().toUpperCase() || null
          : null,
        tax_invoice_address: (values.tax_invoice_address || "").trim(),
        tax_invoice_city: cityFinal,
        tax_invoice_state: values.tax_invoice_state,
        tax_invoice_pincode: pinFinal,
      };
      const raw = mode === "admin"
        ? await updateData(ADMIN.ORG_TAX_INVOICE_UPDATE(orgId), body)
        : await postData(PAYMENTS.TAX_INVOICE_SUBMIT, body);
      const result = raw?.data?.result || raw?.result || raw?.data || raw || {};
      antMessage.success("Tax invoice details saved!");
      onSaved?.(result);
    } catch (err) {
      const detail =
        typeof err === "string"
          ? err
          : err?.detail || err?.message || "Could not save. Please retry.";
      setServerError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Tax invoice details
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            One-time setup before your first purchase. Used to generate
            your GST-compliant tax invoice.
          </Text>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={submit}
            loading={submitting}
          >
            Save & continue
          </Button>
        </div>
      }
      destroyOnClose
      width={620}
      maskClosable={false}
    >
      {serverError && (
        <Alert
          type="error"
          showIcon
          message={serverError}
          closable
          onClose={() => setServerError(null)}
          className="mb-3"
        />
      )}

      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        Once saved, only the platform admin can edit these details.
        Please verify before submitting.
      </Paragraph>

      <Form form={form} layout="vertical" requiredMark>
        {/* Read-only company name pulled from organizations.company_name
            via the parent caller. Helps the user verify they're filling
            tax-invoice details for the correct organization. Never editable
            from this modal — admins can change it elsewhere if needed. */}
        <Form.Item label="Company name" style={{ marginBottom: 12 }}>
          <Input
            value={companyName || ""}
            disabled
            placeholder="Company name"
          />
        </Form.Item>

        {/* "GST" label + Yes/No Switch + GSTIN input on the same row.
            Switch defaults to Yes (most customers are GST-registered).
            GSTIN input is always visible (no reflow) and validation is
            conditional: required only when the switch is on. On save the
            GSTIN value is discarded when the switch is off (see submit()
            below). */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <Form.Item
            name="is_gst_registered"
            label="GST"
            valuePropName="checked"
            style={{ marginBottom: 12, flex: "0 0 auto" }}
          >
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={(checked) => setIsGstRegistered(checked)}
            />
          </Form.Item>
          <Form.Item
            name="tax_invoice_gst_number"
            label="GSTIN"
            dependencies={["is_gst_registered"]}
            style={{ flex: 1, marginBottom: 12 }}
            rules={[
              {
                validator: (_, value) => {
                  if (!isGstRegistered) return Promise.resolve();
                  if (!value || !String(value).trim()) {
                    return Promise.reject(new Error("GSTIN is required"));
                  }
                  const v = String(value).trim().toUpperCase();
                  if (GSTIN_PATTERN.test(v)) return Promise.resolve();
                  return Promise.reject(
                    new Error("Invalid GSTIN format (15-char alphanumeric)"),
                  );
                },
              },
            ]}
            normalize={(v) => (v || "").toUpperCase()}
          >
            <Input
              placeholder="12ABCDE3456F7Z8"
              autoComplete="off"
              maxLength={15}
              disabled={!isGstRegistered}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="tax_invoice_address"
          label="Billing address"
          rules={[
            { required: true, message: "Address is required" },
            { min: 5, message: "Address must be at least 5 characters" },
            { max: 500, message: "Address is too long (max 500 chars)" },
          ]}
        >
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="Street, building, locality"
            autoComplete="off"
          />
        </Form.Item>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Form.Item
            name="tax_invoice_state"
            label="State"
            rules={[{ required: true, message: "State is required" }]}
          >
            <Select
              showSearch
              placeholder="Select state"
              options={INDIA_STATES.map((s) => ({ value: s, label: s }))}
              optionFilterProp="label"
              onChange={(val) => {
                setState(val);
                // Reset downstream city + pincode when state changes
                form.setFieldsValue({
                  tax_invoice_city: undefined,
                  tax_invoice_pincode: undefined,
                });
                setCustomCity("");
                setCustomPin("");
                setCity(null);
              }}
            />
          </Form.Item>

          <Form.Item
            name="tax_invoice_city"
            label="City"
            rules={[{ required: true, message: "City is required" }]}
          >
            <Select
              showSearch
              placeholder={state ? "Select city" : "Select state first"}
              options={cityOptions}
              optionFilterProp="label"
              disabled={!state}
              onChange={handleCityChange}
            />
          </Form.Item>
        </div>

        {/* "Other" city free-text fallback — appears when user picks
            the sentinel in the dropdown. Keeps the form usable even
            for cities not in our curated list. */}
        {cityWatch === OTHER_CITY && (
          <Form.Item label="Type city name">
            <Input
              value={customCity}
              onChange={(e) => setCustomCity(e.target.value)}
              placeholder="Enter city"
              maxLength={140}
            />
          </Form.Item>
        )}

        <Form.Item
          name="tax_invoice_pincode"
          label="Pincode"
          rules={[{ required: true, message: "Pincode is required" }]}
        >
          <Select
            showSearch
            placeholder={
              cityWatch
                ? "Select pincode"
                : "Select city first"
            }
            options={pincodeOptions}
            optionFilterProp="label"
            disabled={!cityWatch}
            onChange={handlePincodeChange}
          />
        </Form.Item>

        {/* Pincode free-text fallback — appears when user picks the
            "Other" sentinel (their city has no curated pincode OR they
            need a different one). Same 6-digit validation as before
            (enforced in submit). */}
        {pinWatch === OTHER_PIN && (
          <Form.Item label="Type pincode">
            <Input
              value={customPin}
              onChange={(e) =>
                setCustomPin(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="Enter 6-digit pincode"
              maxLength={6}
              inputMode="numeric"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
